import type {LucideIcon} from "lucide-react";
import type {ReactNode} from "react";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {cn} from "@/lib/utils";

type Size = "default" | "lg" | "xl" | "full";

const sizeClass: Record<Size, string> = {
  default: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-[min(1280px,calc(100vw-1.5rem))]",
  full: "max-w-[min(1320px,calc(100vw-1.25rem))]"
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  headerExtra?: ReactNode;
  footer?: ReactNode;
  size?: Size;
  children: ReactNode;
};

/**
 * Admin dialog shell. Body uses native overflow-y-auto (not Radix ScrollArea)
 * so tall content scrolls reliably.
 */
export function AdminEditModal({
  open,
  onOpenChange,
  title,
  description,
  icon: Icon,
  headerExtra,
  footer,
  size = "lg",
  children
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "admin-modal flex max-h-[min(92vh,920px)] w-[calc(100vw-1.5rem)] flex-col gap-0 overflow-hidden p-0",
          sizeClass[size]
        )}
      >
        <DialogHeader className="admin-modal__header-wrap shrink-0 space-y-0 text-left">
          <div className="admin-modal__header">
            {Icon ? (
              <div className="admin-modal__icon" aria-hidden>
                <Icon className="admin-modal__icon-svg" />
              </div>
            ) : null}
            <div className="admin-modal__heading min-w-0 flex-1">
              <DialogTitle className="admin-modal__title">{title}</DialogTitle>
              {description ? (
                <DialogDescription asChild>
                  <div className="admin-modal__desc">{description}</div>
                </DialogDescription>
              ) : null}
            </div>
          </div>
          {headerExtra ? <div className="admin-modal__extra">{headerExtra}</div> : null}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-muted/25">
          <div className="px-5 py-5 sm:px-6 admin-animate-fade">{children}</div>
        </div>
        {footer ? (
          <DialogFooter className="admin-modal__footer shrink-0 gap-2 border-t bg-background/95 px-5 py-3.5 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:space-x-0 sm:px-6">
            {footer}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
